import {patchState, signalStore, withLinkedState, withMethods, withState} from "@ngrx/signals";
import {IAMUserState} from "../../../../../shared/types/iam-types/iAMUserState";
import {withDevtools} from "@angular-architects/ngrx-toolkit";
import {ApiService} from "../../../../../shared/services/api.service";
import {inject} from "@angular/core";
import {LanguageService} from "../../../../../shared/services/language.service";
import {take} from "rxjs";
import {IAMGroupInfoResource} from "../../../../../shared/types/iam-types/iAMGroupInfoResource";
import {IAMUserInfoResource} from "../../../../../shared/types/iam-types/iAMUserInfoResource";
import {IAMSectionResource} from "../../../../../shared/types/iam-types/iAMSectionResource";
import {IAMGrant} from "../../../../../shared/types/iam-types/iAMGrant";
import {MembershipPill} from "../../models/membership-pill.interface";
import {IAMUserListResource} from "../../../../../shared/types/iam-types/iAMUserListResource";
import {IAMGroupListResource} from "../../../../../shared/types/iam-types/iAMGroupListResource";
import {IAMGroupUpdateRequest} from "../../../../../shared/types/iam-types/iAMGroupUpdateRequest";
import {IAMUserUpdateRequest} from "../../../../../shared/types/iam-types/iAMUserUpdateRequest";
import {IAMUserGroupResource} from "../../../../../shared/types/iam-types/iAMUserGroupResource";
import {IAMUserResource} from "../../../../../shared/types/iam-types/iAMUserResource";
import {IAMGroupResource} from "../../../../../shared/types/iam-types/iAMGroupResource";
import {IAMRightsUpdateRequest} from "../../../../../shared/types/iam-types/iAMRightsUpdateRequest";
import {IAMUserActivateRequest} from "../../../../../shared/types/iam-types/iAMUserActivateRequest";
import {mapSectionRightsToUpdateRequest} from "../../mapper/rights-mapper";
import {CapabilitiesService} from "../../../../../shared/services/capabilities.service";

export type RightsEntityType = 'user' | 'group';

export interface RightsStore {
  userList: IAMUserListResource[];
  selectedUserIds: number[];
  userListLoading: boolean;
  userListSettings: {
    state: IAMUserState,
    search: string,
    skip: number,
    limit: number,
    endReached: boolean
  },
  groupList: IAMGroupListResource[];
  selectedGroupIds: number[];
  groupListLoading: boolean;
  groupListSettings: {
    search: string,
    skip: number,
    limit: number,
    endReached: boolean
  },
  listView: 'users' | 'groups';
  activeEntityId: number | null;
  activeEntityType: RightsEntityType | null;
  activeUserDetails: IAMUserResource | null;
  activeGroupDetails: IAMGroupResource | null;
  activeUserGroups: IAMUserGroupResource[];
  activeGroupUsers: IAMUserInfoResource[];
  activeUserRights: IAMSectionResource[];
  activeGroupRights: IAMSectionResource[];
  activeMembershipLoading: boolean;
  activeRightsLoading: boolean;
}

const initialState: RightsStore = {
  userList: [],
  selectedUserIds: [],
  userListLoading: false,
  userListSettings: {
    state: IAMUserState.Active,
    search: '',
    skip: 0,
    limit: 25,
    endReached: false
  },
  groupList: [],
  selectedGroupIds: [],
  groupListLoading: false,
  groupListSettings: {
    search: '',
    skip: 0,
    limit: 25,
    endReached: false
  },
  listView: 'users',
  activeEntityId: null,
  activeEntityType: null,
  activeUserDetails: null,
  activeGroupDetails: null,
  activeUserGroups: [],
  activeGroupUsers: [],
  activeUserRights: [],
  activeGroupRights: [],
  activeMembershipLoading: false,
  activeRightsLoading: false
};

export const RightsStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('RightsStore'),
  withLinkedState((store) => {
    const lang: LanguageService = inject(LanguageService);

    return {
      listEntities: () => store.listView() === 'users' ? store.userList() : store.groupList(),
      selectedIds: () => store.listView() === 'users' ? store.selectedUserIds() : store.selectedGroupIds(),
      selectedCount: () => store.listView() === 'users' ? store.selectedUserIds().length : store.selectedGroupIds().length,
      hasSelection: () => store.listView() === 'users' ? store.selectedUserIds().length > 0 : store.selectedGroupIds().length > 0,
      activeEntityName: () => {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null) {
          return null;
        }

        if (activeEntityType === 'user') {
          const userDetails: IAMUserResource | null = store.activeUserDetails();
          if (userDetails !== null && userDetails.id === activeEntityId) {
            return `${userDetails.last_name} ${userDetails.first_name}`;
          }

          const user: IAMUserListResource | undefined = store.userList().find((u) => u.id === activeEntityId);
          return user ? `${user.last_name} ${user.first_name}` : null;
        }

        const groupDetails: IAMGroupResource | null = store.activeGroupDetails();
        if (groupDetails !== null && groupDetails.id === activeEntityId) {
          return groupDetails.name;
        }

        const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
        return group ? group.name : null;
      },
      activeEntityAvatar: (): string => {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null) {
          return '';
        }

        if (activeEntityType === 'user') {
          const userDetails: IAMUserResource | null = store.activeUserDetails();
          if (userDetails !== null && userDetails.id === activeEntityId) {
            return userDetails?.avatar?.path !== undefined ? userDetails.avatar.path : '';
          }

          const user: IAMUserListResource | undefined = store.userList().find((u) => u.id === activeEntityId);
          return user?.avatar?.path !== undefined ? user?.avatar?.path : ''
        }

        const groupDetails: IAMGroupResource | null = store.activeGroupDetails();
        if (groupDetails !== null && groupDetails.id === activeEntityId) {
          return groupDetails?.avatar?.path !== undefined ? groupDetails.avatar.path : '';
        }

        const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
        return group?.avatar?.path !== undefined ? group?.avatar?.path : ''
      },
      activeEntitySubtitle: (): string => {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null) {
          return '';
        }

        if (activeEntityType === 'user') {
          const userDetails: IAMUserResource | null = store.activeUserDetails();
          if (userDetails !== null && userDetails.id === activeEntityId) {
            return `${lang.getTranslationByKey('USER')} • ${userDetails.admin ? lang.getTranslationByKey('ADMIN') + ' •' : ''} ${new Date(userDetails.created_at).toLocaleString()}`;
          }

          const user: IAMUserListResource | undefined = store.userList().find((u) => u.id === activeEntityId);
          if (user !== undefined) {
            return `${lang.getTranslationByKey('USER')} • ${new Date(user.created_at).toLocaleString()}`;
          } else {
            return `${lang.getTranslationByKey('USER')}`;
          }
        }

        const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
        if(group !== undefined) {
          return `${lang.getTranslationByKey('GROUP')} • ${new Date(group.created_at).toLocaleString()}`;
        } else {
          return `${lang.getTranslationByKey('GROUP')}`;
        }
      },
      activeEntityDescription: (): string | null => {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null || activeEntityType === 'user') {
          return null;
        }

        const groupDetails: IAMGroupResource | null = store.activeGroupDetails();
        if (groupDetails !== null && groupDetails.id === activeEntityId) {
          return groupDetails.description;
        }

        const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
        return group?.description ?? null;
      },
      activeMembershipEntities: (): MembershipPill[] => {
        if (store.activeEntityType() === 'user') {
          const userDetails: IAMUserResource | null = store.activeUserDetails();
          if (userDetails !== null && store.activeEntityId() === userDetails.id) {
            return userDetails.groups.map((group) => ({
              id: group.id,
              name: group.name,
              avatarUrl: group?.avatar?.path
            }));
          }

          return store.activeUserGroups().map((group) => ({
            id: group.id,
            name: group.name,
            avatarUrl: group?.avatar?.path
          }));
        }

        if (store.activeEntityType() === 'group') {
          const groupDetails: IAMGroupResource | null = store.activeGroupDetails();
          if (groupDetails !== null && store.activeEntityId() === groupDetails.id) {
            return groupDetails.users.map((user) => ({
              id: user.id,
              name: `${user.last_name} ${user.first_name}`,
              avatarUrl: user?.avatar?.path
            }));
          }

          return store.activeGroupUsers().map((user) => ({
            id: user.id,
            name: `${user.last_name} ${user.first_name}`,
            avatarUrl: user?.avatar?.path
          }));
        }

        return [];
      },
    } 
  }),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const capabilitiesService: CapabilitiesService = inject(CapabilitiesService);

    const canManageGroups = (): boolean => capabilitiesService.capabilities().settings.iam.groups;
    const canAccessUsers = (): boolean => {
      const userManagement = capabilitiesService.capabilities().settings.iam;
      return userManagement.users || userManagement.user_rights;
    };
    const canEditUserRights = (): boolean => capabilitiesService.capabilities().settings.iam.user_rights;
    const canEditGroupRights = (): boolean => capabilitiesService.capabilities().settings.iam.group_rights;

    return {
      //#region resets
      resetStore() {
        patchState(store, initialState);
      },
      resetUserList() {
        patchState(store, {
          userList: [],
          selectedUserIds: [],
          userListLoading: false,
          userListSettings: {
            ...store.userListSettings(),
            skip: 0,
            endReached: false
          }
        });
        this.clearUserSelection();
      },
      resetGroupList() {
        patchState(store, {
          groupList: [],
          selectedGroupIds: [],
          groupListLoading: false,
          groupListSettings: {
            ...store.groupListSettings(),
            skip: 0,
            endReached: false
          }
        });
        this.clearGroupSelection();
      },
      //#endregion resets
      init() {
        if (canAccessUsers()) {
          this.loadUserList();
        }
        if (canManageGroups()) {
          this.loadGroupList();
        }
      },
      //#region loading
      loadMoreListEntries() {
        if(store.listView() === 'users') {
          if (store.userListLoading() || store.userListSettings().endReached) {
            return;
          }
          this.loadUserList();
        } else {
          if (store.groupListLoading() || store.groupListSettings().endReached) {
            return;
          }
          this.loadGroupList();
        }
      },
      loadUserList(onLoaded?: (userList: IAMUserListResource[]) => void) {
        if (!canAccessUsers()) {
          return;
        }

        if (store.userListLoading() || store.userListSettings().endReached) {
          return;
        }

        patchState(store, {userListLoading: true});
        api.getIAMUserList(
          store.userListSettings().state,
          store.userListSettings().search, 
          store.userListSettings().skip, 
          store.userListSettings().limit
        )
        .pipe(take(1))
        .subscribe({
          next: (userList) => {
            patchState(store, (state: RightsStore) => ({
              ...state,
              userListLoading: false,
              userList: [...state.userList, ...userList],
              userListSettings: {
                ...state.userListSettings,
                skip: state.userListSettings.skip + userList.length,
                endReached: userList.length < state.userListSettings.limit
              }
            }));
            onLoaded?.(userList);
          },
          error: (error) => {
            console.error('Failed to load user list:', error);
            patchState(store, {userListLoading: false});
          }
        });
      },
      loadGroupList(onLoaded?: (groupList: IAMGroupListResource[]) => void) {
        if (!canManageGroups()) {
          return;
        }

        if (store.groupListLoading() || store.groupListSettings().endReached) {
          return;
        }

        patchState(store, {groupListLoading: true});
        api.getIAMGroupList(
          store.groupListSettings().search, 
          store.groupListSettings().skip, 
          store.groupListSettings().limit
        )
        .pipe(take(1))
        .subscribe({
          next: (groupList) => {
            patchState(store, (state: RightsStore) => ({
              ...state,
              groupListLoading: false,
              groupList: [...state.groupList, ...groupList],
              groupListSettings: {
                ...state.groupListSettings,
                skip: state.groupListSettings.skip + groupList.length,
                endReached: groupList.length < state.groupListSettings.limit
              }
            }));
            onLoaded?.(groupList);
          },
          error: (error) => {
            console.error('Failed to load group list:', error);
            patchState(store, {groupListLoading: false});
          }});  
      },
      //#endregion loading
      //#region selections
      setUserSelected(id: number, selected: boolean) {
        const ids: number[] = store.selectedUserIds();
        if (selected && !ids.includes(id)) {
          patchState(store, {selectedUserIds: [...ids, id]});
          return;
        }

        if (!selected && ids.includes(id)) {
          patchState(store, {selectedUserIds: ids.filter((x) => x !== id)});
        }
      },
      setGroupSelected(id: number, selected: boolean) {
        const ids: number[] = store.selectedGroupIds();
        if (selected && !ids.includes(id)) {
          patchState(store, {selectedGroupIds: [...ids, id]});
          return;
        }

        if (!selected && ids.includes(id)) {
          patchState(store, {selectedGroupIds: ids.filter((x) => x !== id)});
        }
      },
      clearUserSelection() {
        patchState(store, {selectedUserIds: []});
      },
      clearGroupSelection() {
        patchState(store, {selectedGroupIds: []});
      },
      clearSelection() {
        if (store.listView() === 'users') {
          this.clearUserSelection();
        } else {
          this.clearGroupSelection();
        }
      },
      deactivateSelection(onSuccess?: () => void) {
        if (store.listView() !== 'users' || store.selectedUserIds().length === 0) {
          return;
        }

        const payload: IAMUserActivateRequest = {
          activate: false,
          user_ids: store.selectedUserIds(),
        };

        api.updateIAMUserActive(payload)
          .pipe(take(1))
          .subscribe({
            next: () => {
              if (onSuccess) {
                this.clearUserSelection();
                onSuccess();
                return;
              }
              this.clearUserSelection();
              this.resetUserList();
              this.loadUserList();
            },
            error: (err) => {
              console.error(err);
            }
          });
      },
      activateSelection(onSuccess?: () => void) {
        if (store.listView() !== 'users' || store.selectedUserIds().length === 0) {
          return;
        }

        const payload: IAMUserActivateRequest = {
          activate: true,
          user_ids: store.selectedUserIds(),
        };

        api.updateIAMUserActive(payload)
          .pipe(take(1))
          .subscribe({
            next: () => {
              if (onSuccess) {
                this.clearUserSelection();
                onSuccess();
                return;
              }
              this.clearUserSelection();
              this.resetUserList();
              this.loadUserList();
            },
            error: (err) => {
              console.error(err);
            }
          });
      },
      deleteSelection() {
        if(store.hasSelection()){
          if(store.listView() === 'groups' && store.selectedGroupIds().length > 0) {
            api.deleteIAMGroups(store.selectedGroupIds())
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.clearGroupSelection();
                this.resetGroupList();
                this.loadGroupList((groupList: IAMGroupListResource[]) => {
                  const firstGroupId = groupList[0]?.id ?? null;
                  this.setActiveEntity(firstGroupId, 'group');
                });
              },
              error: (err) => {
                console.error(err);
              }
            })
          } else if(store.listView() === 'users' && store.selectedUserIds().length > 0) {
            api.deleteIAMUsers(store.selectedUserIds())
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.clearUserSelection();
                this.resetUserList();
                this.loadUserList((userList: IAMUserListResource[]) => {
                  const firstUserId = userList[0]?.id ?? null;
                  this.setActiveEntity(firstUserId, 'user');
                });
              },
              error: (err) => {
                console.error(err);
              }
            })
          } else {
            console.error('Cannot delete selection because there is a mismatch between the view and the selected entries');
          }
        } else {
          console.error('Cannot delete selection because there exists no selection');
        }
      },
      //#endregion selections
      //#region store updates
      setUserListSearch(search: string) {
        if (!canAccessUsers()) {
          return;
        }

        if (store.userListSettings().search === search) {
          return;
        }

        patchState(store, {
          userListSettings: {
            ...store.userListSettings(),
            search: search,
          }
        });
        this.resetUserList();
        this.loadUserList();
      },
      setUserListState(state: IAMUserState) {
        if (!canAccessUsers()) {
          return;
        }

        if (store.userListSettings().state === state) {
          return;
        }

        patchState(store, {
          userListSettings: {
            ...store.userListSettings(),
            state: state
          }
        });
        this.resetUserList();
        this.loadUserList();
      },
      setGroupListSearch(search: string) {
        if (!canManageGroups()) {
          return;
        }

        if (store.groupListSettings().search === search) {
          return;
        }

        patchState(store, {
          groupListSettings: {
            ...store.groupListSettings(),
            search: search
          }
        });
        this.resetGroupList();
        this.loadGroupList();
      },
      setListView(view: 'users' | 'groups') {
        if (store.listView() === view) {
          return;
        }

        patchState(store, {
          listView: view,
        });

        if (view === 'users' && store.userList().length === 0) {
          this.loadUserList();
        }

        if (view === 'groups' && store.groupList().length === 0) {
          this.loadGroupList();
        }
      },
      setSearch(search: string) {
        if(store.listView() === 'users') {
          this.setUserListSearch(search);
        } else {
          this.setGroupListSearch(search);
        }
      },
      setActiveEntity(id: number | null, type?: RightsEntityType) {
        if (id === null) {
          patchState(store, {
            activeEntityId: null,
            activeEntityType: null,
            activeUserDetails: null,
            activeGroupDetails: null,
            activeUserGroups: [],
            activeGroupUsers: [],
            activeUserRights: [],
            activeGroupRights: [],
            activeMembershipLoading: false,
            activeRightsLoading: false
          });
          return;
        }

        const resolvedType: RightsEntityType | null = type
          ?? (store.userList().some((u) => u.id === id) ? 'user' : null)
          ?? (store.groupList().some((g) => g.id === id) ? 'group' : null);

        if (!resolvedType) {
          return;
        }

        const entityUnchanged = store.activeEntityId() === id && store.activeEntityType() === resolvedType;
        if (entityUnchanged) {
          return;
        }

        patchState(store, {
          activeEntityId: id,
          activeEntityType: resolvedType,
          activeMembershipLoading: true,
          activeRightsLoading: resolvedType === 'user' ? canEditUserRights() : canEditGroupRights()
        });

        if (resolvedType === 'user') {
          patchState(store, {
            activeUserDetails: null,
            activeGroupDetails: null,
            activeGroupUsers: [],
            activeGroupRights: []
          });
          this.loadActiveUserDetails(id);
          if (canEditUserRights()) {
            this.loadActiveUserRights(id);
          }
          return;
        }

        patchState(store, {
          activeUserDetails: null,
          activeGroupDetails: null,
          activeUserGroups: [],
          activeUserRights: []
        });
        this.loadActiveGroupDetails(id);
        if (canEditGroupRights()) {
          this.loadActiveGroupRights(id);
        }
      },
      setActiveUserRights(rights: IAMSectionResource[]) {
        patchState(store, {activeUserRights: rights});
      },
      setActiveGroupRights(rights: IAMSectionResource[]) {
        patchState(store, {activeGroupRights: rights});
      },
      setActiveUserRightGrant(sectionId: string, rightId: string, grant: IAMGrant) {
        const nextRights = store.activeUserRights().map((section) => {
          if (section.id !== sectionId) return section;

          return {
            ...section,
            rights: section.rights.map((right) =>
              right.id === rightId
                ? {...right, grant}
                : right
            )
          };
        });

        patchState(store, {activeUserRights: nextRights});
      },
      setActiveGroupRightGrant(sectionId: string, rightId: string, grant: IAMGrant) {
        const nextRights = store.activeGroupRights().map((section) => {
          if (section.id !== sectionId) return section;

          return {
            ...section,
            rights: section.rights.map((right) =>
              right.id === rightId
                ? {...right, grant}
                : right
            )
          };
        });

        patchState(store, {activeGroupRights: nextRights});
      },
      loadActiveUserGroups(userId: number) {
        api.getIAMUserGroups(userId)
          .pipe(take(1))
          .subscribe({
            next: (groups) => {
              if (store.activeEntityId() !== userId || store.activeEntityType() !== 'user') {
                return;
              }

              patchState(store, {
                activeUserGroups: groups,
                activeMembershipLoading: false
              });
            },
            error: (error) => {
              console.error('Failed to load user groups:', error);
              if (store.activeEntityId() === userId && store.activeEntityType() === 'user') {
                patchState(store, {activeMembershipLoading: false});
              }
            }
          });
      },
      loadActiveUserDetails(userId: number) {
        api.getIAMUser(userId)
          .pipe(take(1))
          .subscribe({
            next: (user) => {
              if (store.activeEntityId() !== userId || store.activeEntityType() !== 'user') {
                return;
              }

              patchState(store, {
                activeUserDetails: user,
                activeUserGroups: user.groups,
                activeMembershipLoading: false
              });
            },
            error: (error) => {
              console.error('Failed to load user details:', error);
              if (store.activeEntityId() === userId && store.activeEntityType() === 'user') {
                patchState(store, {activeMembershipLoading: false});
              }
            }
          });
      },
      loadActiveGroupDetails(groupId: number) {
        api.getIAMGroup(groupId)
          .pipe(take(1))
          .subscribe({
            next: (group) => {
              if (store.activeEntityId() !== groupId || store.activeEntityType() !== 'group') {
                return;
              }

              patchState(store, {
                activeGroupDetails: group,
                activeGroupUsers: group.users,
                activeMembershipLoading: false
              });
            },
            error: (error) => {
              console.error('Failed to load group details:', error);
              if (store.activeEntityId() === groupId && store.activeEntityType() === 'group') {
                patchState(store, {activeMembershipLoading: false});
              }
            }
          });
      },
      loadActiveUserRights(userId: number) {
        if (!canEditUserRights()) {
          patchState(store, {activeRightsLoading: false});
          return;
        }

        api.getIAMUserRights(userId)
          .pipe(take(1))
          .subscribe({
            next: (rights) => {
              if (store.activeEntityId() !== userId || store.activeEntityType() !== 'user') {
                return;
              }

              patchState(store, {
                activeUserRights: rights,
                activeRightsLoading: false
              });
            },
            error: (error) => {
              console.error('Failed to load user rights:', error);
              if (store.activeEntityId() === userId && store.activeEntityType() === 'user') {
                patchState(store, {activeRightsLoading: false});
              }
            }
          });
      },
      loadActiveGroupUsers(groupId: number) {
        api.getIAMGroupUsers(groupId)
          .pipe(take(1))
          .subscribe({
            next: (users) => {
              if (store.activeEntityId() !== groupId || store.activeEntityType() !== 'group') {
                return;
              }

              patchState(store, {
                activeGroupUsers: users,
                activeMembershipLoading: false
              });
            },
            error: (error) => {
              console.error('Failed to load group users:', error);
              if (store.activeEntityId() === groupId && store.activeEntityType() === 'group') {
                patchState(store, {activeMembershipLoading: false});
              }
            }
          });
      },
      loadActiveGroupRights(groupId: number) {
        if (!canEditGroupRights()) {
          patchState(store, {activeRightsLoading: false});
          return;
        }

        api.getIAMGroupRights(groupId)
          .pipe(take(1))
          .subscribe({
            next: (rights: IAMSectionResource[]) => {
              if (store.activeEntityId() !== groupId || store.activeEntityType() !== 'group') {
                return;
              }

              patchState(store, {
                activeGroupRights: rights,
                activeRightsLoading: false
              });
            },
            error: (error: unknown) => {
              console.error('Failed to load group rights:', error);
              if (store.activeEntityId() === groupId && store.activeEntityType() === 'group') {
                patchState(store, {activeRightsLoading: false});
              }
            }
          });
      },
      saveIAMGroup(
        data: IAMGroupUpdateRequest,
        groupId: number | null = null,
        onSuccess?: (groupId: number) => void
      ) {
        const wasSameActiveGroup =
          groupId !== null &&
          store.activeEntityId() === groupId &&
          store.activeEntityType() === 'group';

        const request$ = groupId === null
          ? api.createIAMGroup(data)
          : api.updateIAMGroup(groupId, data);

        request$
          .pipe(take(1))
          .subscribe({
            next: (group: IAMGroupInfoResource) => {
              patchState(store, {listView: 'groups'});
              this.resetGroupList();
              this.loadGroupList();
              this.setActiveEntity(group.id, 'group');
              if (wasSameActiveGroup) {
                patchState(store, {activeMembershipLoading: true});
                this.loadActiveGroupDetails(group.id);
              }
              onSuccess?.(group.id);
            },
            error: (error) => {
              console.error('Failed to save group:', error);
            }
          });
      },
      saveIAMUser(
        data: IAMUserUpdateRequest,
        userId: number | null = null,
        onSuccess?: (userId: number) => void
      ) {
        const wasSameActiveUser =
          userId !== null &&
          store.activeEntityId() === userId &&
          store.activeEntityType() === 'user';

        const request$ = userId === null
          ? api.createIAMUser(data)
          : api.updateIAMUser(userId, data);

        request$
          .pipe(take(1))
          .subscribe({
            next: (user) => {
              patchState(store, {listView: 'users'});
              this.resetUserList();
              this.loadUserList();
              this.setActiveEntity(user.id, 'user');
              if (wasSameActiveUser) {
                patchState(store, {activeMembershipLoading: true});
                this.loadActiveUserDetails(user.id);
              }
              onSuccess?.(user.id);
            },
            error: (error) => {
              console.error('Failed to save user:', error);
            }
          });
      },
      //#endregion store updates
      //#region api updates
      saveUserRightsUpdate(): void {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null || activeEntityType !== 'user') {
          return;
        }

        const userDetails: IAMUserResource | null = store.activeUserDetails();
        if (userDetails !== null && userDetails.id === activeEntityId) {
          const mappedUserRights: IAMRightsUpdateRequest = mapSectionRightsToUpdateRequest(store.activeUserRights(), false);
          api.updateIAMUserRights(userDetails.id, mappedUserRights)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                activeMembershipLoading: true,
                activeRightsLoading: true
              });
              this.loadActiveUserDetails(userDetails.id);
              this.loadActiveUserRights(userDetails.id);
            },
            error: (err) => {
              console.error(err);
            }
          })
        }
      },
      saveGroupRightsUpdate(): void {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null || activeEntityType !== 'group') {
          return;
        }

        const groupDetails: IAMGroupResource | null = store.activeGroupDetails();
        if (groupDetails !== null && groupDetails.id === activeEntityId) {
          const mappedGroupRights: IAMRightsUpdateRequest = mapSectionRightsToUpdateRequest(store.activeGroupRights(), true);
          api.updateIAMGroupRights(groupDetails.id, mappedGroupRights)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                activeMembershipLoading: true,
                activeRightsLoading: true
              });
              this.loadActiveGroupDetails(groupDetails.id);
              this.loadActiveGroupRights(groupDetails.id);
            },
            error: (err) => {
              console.error(err);
            }
          })
        }
      },
      removeMembership(id: number): void {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null) {
          return;
        }

        if (activeEntityType === 'user') {
          const user: IAMUserListResource | undefined = store.userList().find((u) => u.id === activeEntityId);
          if (user !== undefined) {
            this.detachGroupFromUser(user.id, id);
          }
        } else {
          const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
          if(group !== undefined) {
            this.detachGroupFromUser(id, group.id);
          }
        }
      },
      detachGroupFromUser(userId: number, groupId: number) {
        api.removeIAMGroupFromUser(userId, groupId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            patchState(store, {activeMembershipLoading: true});
            if (store.activeEntityType() === 'user' && store.activeEntityId() === userId) {
              this.loadActiveUserDetails(userId);
            } else if (store.activeEntityType() === 'group' && store.activeEntityId() === groupId) {
              this.loadActiveGroupDetails(groupId);
            } else {
              patchState(store, {activeMembershipLoading: false});
            }
          },
          error: (err) => {
            console.error(err);
          }
        })
      },
      addMembership(id: number): void {
        const activeEntityId: number | null = store.activeEntityId();
        const activeEntityType: RightsEntityType | null = store.activeEntityType();

        if (activeEntityId === null || activeEntityType === null) {
          return;
        }

        if (activeEntityType === 'user') {
          const user: IAMUserListResource | undefined = store.userList().find((u) => u.id === activeEntityId);
          if (user !== undefined) {
            this.addGroupToUser(user.id, id);
          }
        } else {
          const group: IAMGroupListResource | undefined = store.groupList().find((g) => g.id === activeEntityId);
          if(group !== undefined) {
            this.addGroupToUser(id, group.id);
          }
        }
      },
      addGroupToUser(userId: number, groupId: number) {
        api.addIAMGroupToUser(userId, groupId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            patchState(store, {activeMembershipLoading: true});
            if (store.activeEntityType() === 'user' && store.activeEntityId() === userId) {
              this.loadActiveUserDetails(userId);
            } else if (store.activeEntityType() === 'group' && store.activeEntityId() === groupId) {
              this.loadActiveGroupDetails(groupId);
            } else {
              patchState(store, {activeMembershipLoading: false});
            }
          },
          error: (err) => {
            console.error(err);
          }
        })
      },
      //#endregion api updates
    };
  })
);
